import type { ConfigurationSetup } from './config/config-setup.types';
import { TeoEventTarget } from './utility/TeoEventTarget';

export interface DynamicConfigOptions {
  alwaysCheckBrandFit: boolean;
  evaluationProfileId: number;
  subscriptionName: string;
  textConfigOptions: ConfigurationSetup | null;
  useExpParser: boolean;

  // --- Flags ---
  brandFitEnabled: boolean;
  textGeneratorEnabled: boolean;
  whitelistEnabled: boolean;
  dictionaryEnabled: boolean;
  plainLanguageEnabled: boolean;
}

export type DynamicConfigEvents = {
  [K in keyof DynamicConfigOptions]: (value: DynamicConfigOptions[K]) => void;
};

export class DynamicConfig extends TeoEventTarget<DynamicConfigEvents> {
  public readonly options: DynamicConfigOptions;
  private oldValues: Partial<DynamicConfigOptions> = {};

  constructor(options: Partial<DynamicConfigOptions> = {}) {
    super();
    this.options = {
      alwaysCheckBrandFit: false,
      evaluationProfileId: 3,
      subscriptionName: '',
      textConfigOptions: null,
      useExpParser: false,
      // Flags
      brandFitEnabled: false,
      textGeneratorEnabled: false,
      whitelistEnabled: true,
      dictionaryEnabled: true,
      plainLanguageEnabled: false,
      ...options,
    };
  }

  public getOldValue<K extends keyof DynamicConfigOptions>(
    property: K,
  ): DynamicConfigOptions[K] | undefined {
    return this.oldValues[property];
  }

  public update<K extends keyof DynamicConfigOptions>(
    property: K,
    value: DynamicConfigOptions[K],
    skipCheck: boolean = false,
  ): void {
    const oldValue = this.options[property];
    if (skipCheck || oldValue !== value) {
      this.oldValues[property] = oldValue;
      this.options[property] = value;
      this.triggerEvent(property, value);
    }
  }

  public updateOptions(config: Partial<DynamicConfigOptions>) {
    const keys = Object.keys(this.options) as (keyof DynamicConfigOptions)[];
    for (const key of keys) {
      const value = config[key];
      if (value !== undefined) {
        this.update(key, value);
      }
    }
  }
}
